import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { AuthService } from 'app/core/auth/auth.service';
import { of } from 'rxjs';
import { timeout, catchError } from 'rxjs/operators';
import { PrivacyService } from './privacy.service';
import { PrivacyAcceptanceDialog } from './privacy-acceptance.dialog';

/**
 * PolicyAcceptanceGuard — LPD Ley 21.719 (fire-and-forget).
 *
 * Estrategia: el guard SIEMPRE retorna `true` → la navegación completa de inmediato,
 * el splash-screen de Fuse se oculta al primer NavigationEnd.
 *
 * Asincrónicamente: si hay pending, abre modal overlay sobre el layout. El modal
 * tiene `disableClose: true` → el user no puede cerrarlo sin aceptar o rechazar.
 *
 * Ventajas sobre bloquear la navegación:
 *   - Reload durante el modal NO deja splash colgado.
 *   - HTTP lento no bloquea el arranque de la app.
 *   - Tolerante a fallas de coruscant (catchError + timeout 3s).
 */
const GUARD_WHITELIST = ['/sign-out', '/unlock-session', '/sign-in', '/landing'];

// Singleton — evita abrir múltiples dialogs si el guard corre por cada ruta anidada.
let _modalOpen = false;

export const PolicyAcceptanceGuard: CanActivateFn = (_route, state) => {
    const router = inject(Router);
    const dialog = inject(MatDialog);
    const privacy = inject(PrivacyService);
    const auth = inject(AuthService);

    if (GUARD_WHITELIST.some((p) => state.url === p || state.url.startsWith(p + '?'))) {
        return true;
    }
    if (_modalOpen) return true;

    // Fire-and-forget: el chequeo de políticas corre en background.
    privacy
        .getPendingAcceptances()
        .pipe(
            timeout({ first: 3000, with: () => of([]) }),
            catchError(() => of([])),
        )
        .subscribe((pending) => {
            if (!pending.length || _modalOpen) return;

            _modalOpen = true;
            const ref = dialog.open(PrivacyAcceptanceDialog, {
                width: '920px',
                maxWidth: '95vw',
                disableClose: true,
                panelClass: 'privacy-acceptance-panel',
            });

            ref.afterClosed().subscribe((result: any) => {
                _modalOpen = false;
                if (!result?.accepted) {
                    auth.signOut();
                    router.navigate(['/sign-in']);
                }
            });
        });

    return true;
};
